import type { ThemeUIStyleObject } from '@theme-ui/core';

import { FacebookIcon } from '@change/ds-icons';

import { Link } from '@change-corgi/design-system-legacy/components/actions';
import { Image } from '@change-corgi/design-system-legacy/components/media';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading } from '@change-corgi/design-system-legacy/typography';

import type { Recommendation } from '../../../../hooks/useShareRecommendations';
import { useShareRecommendationsTracking } from '../../../../hooks/useShareRecommendationsTracking';

const styles: Record<string, ThemeUIStyleObject> = {
	iconContainer: {
		alignItems: 'center',
		height: '100%',
		justifyContent: 'center',
		width: '100%',
	},
	cardContainer: {
		border: '1px solid',
		borderColor: 'neutral-grey400',
		borderRadius: '8px',
		height: '70px',
		maxWidth: [null, '430px'],
		width: '100%',
		/* eslint-disable-next-line @typescript-eslint/naming-convention */
		'&:hover': {
			boxShadow: '0px 10px 20px 0px #18191F1A',
			cursor: 'pointer',
		},
	},
	innerCardContainer: {
		backgroundColor: 'neutral-grey400',
		borderRadius: '8px 0px 0px 8px',
		flexShrink: 0,
		height: '100%',
		width: ['95px', '125px'],
	},
	imageContainer: {
		justifyContent: 'center',
		borderRadius: '7px 0px 0px 7px',
		height: '100%',
		overflow: 'hidden',
		width: '100%',
	},
	image: {
		height: '100%',
		objectFit: 'cover',
		top: 0,
		width: '100%',
		borderRightStyle: 'solid',
		borderRightWidth: '1px',
		borderRightColor: 'neutral-grey100',
	},
	headingText: {
		alignItems: 'center',
		flex: 1,
		height: '100%',
		paddingLeft: '16px',
		paddingRight: '16px',
		width: 'auto',
	},
};

type Props = {
	recommendation: Recommendation;
};

function defaultIconImage() {
	return (
		<div
			className="flex h-full w-full items-center justify-center bg-[#D1E4FC]"
			data-testid="facebook-group-card-default-icon"
		>
			<div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-white bg-[#1877F2]">
				<FacebookIcon className="text-white" size={24} />
			</div>
		</div>
	);
}

/*
	Using blank alt attribute to prevent screen readers from reading the images. This is needed because the
	images are decorative and the whole component is wrapped in a link. Screen readers would announce twice or
	even three times the same information if we don't use blank alt attribute.
 */
export function FacebookCard({ recommendation }: Props) {
	const { metadata } = recommendation;
	const {
		actions: { handleLinkClick },
	} = useShareRecommendationsTracking();

	return (
		<Link
			sx={{ textDecoration: 'none' }}
			to={recommendation.url}
			target="_blank"
			onClick={() => {
				handleLinkClick(recommendation.name, 'facebook_group', metadata.source);
			}}
			tabIndex={0}
			aria-label={`Facebook Group: ${recommendation.name}`}
			data-testid="facebook-group-card"
			data-qa="facebook-group-card"
		>
			<Flex sx={styles.cardContainer}>
				<Box sx={styles.innerCardContainer}>
					{/* Simulate an inner border */}
					<Flex sx={styles.imageContainer}>
						{!metadata.imageUrl && defaultIconImage()}
						{metadata.imageUrl && (
							<Image alt="" src={metadata.imageUrl} sx={styles.image} data-testid="facebook-group-card-image" />
						)}
					</Flex>
				</Box>
				<Flex sx={styles.headingText}>
					<Heading
						as="h5"
						size={['h6', 'h5']}
						color="typography-primary"
						lineClamp={2}
						sx={{ fontWeight: '700', letterSpacing: '-1%', lineHeight: '120%' }}
						data-testid="facebook-group-card-heading"
					>
						{recommendation.name}
					</Heading>
				</Flex>
			</Flex>
		</Link>
	);
}
